import App from "../App";
import AudioSequence from "./AudioSequence";
import { GameHowl } from "./GameHowl";
import TempoDetector from "./TempoDetector";
import Utils from "./Utils";

export default abstract class AudioSequencer {
    protected sequence: AudioSequence;

    public get loop(): boolean {
        return this.sequence.loop;
    }

    public set loop(value: boolean) {
        this.sequence.loop = value;
    }

    public get volume(): number {
        return this.sequence.isMuted ? 0 : this.sequence.volume;
    }

    public set volume(value: number) {
        this.sequence.volume = this.sequence.isMuted ? 0 : value;
    }

    get volumeOverride(): number {
        if (this.sequence.isMuted) {
            return 0;
        }

        return App.config.soundVolumeOverride[this.sequence.name] || 1;
    }

    public get name(): string {
        return this.sequence.name;
    }

    public get currentSound(): GameHowl {
        return this.sequence.currentSound;
    }

    get trackIndex(): number {
        return this.sequence.trackIndex;
    }

    public async init(masterSoundId: string, tracks: GameHowl[], bpm: number, bpsMultiplier: number = 1): Promise<void> {
        if (Utils.isNullOrWhiteSpace(masterSoundId)) {
            throw new Error("masterSoundId can not be null or white-space!");
        }

        if (!tracks || tracks.length < 2) {
            throw new Error("tracks array must contain at least 2 GameHowls!");
        }

        if (!bpm || bpm <= 0) {
            bpm = await this.detectTempo(tracks);
        }

        this.sequence = new AudioSequence({
            masterSoundId,
            tracks,
            sequencer: this,
            bpm,
            bpsMultiplier
        });
    }

    public getSequence(): AudioSequence {
        return this.sequence;
    }

    public changeTrack(trackIndex: number, fadeTimeInSeconds: number) {
        if (trackIndex == this.sequence.trackIndex) {
            return;
        }

        this.crossfade(this.sequence.tracks[this.sequence.trackIndex], this.sequence.tracks[trackIndex], fadeTimeInSeconds);
        this.sequence.trackIndex = trackIndex;
    }

    protected crossfade(currrent: GameHowl, next: GameHowl, fadeTimeInSeconds: number) {
        this.sequence.tracks.forEach(track => track.stop());
        next.volume(this.volumeOverride);
        next.play();
    }

    protected async detectTempo(tracks: GameHowl[]): Promise<number> {
        let sum = 0;
        let count = 0;
        for (let index = 0; index < tracks.length; index++) {
            const tempo = await TempoDetector.detectTempo(tracks[index]);
            if (tempo > 0) {
                sum += tempo;
                count++;
            }
        }

        if (count == 0) {
            /* Example for hardcoded tempo at asset-config.json
            "music": [
                // Music, using audio sequencer
                [
                "idleBackgroundMusic", // Music asset id
                [
                    "assets/audio/idleBackgroundMusic_0.m4a", // TRACK SOURCE FILE
                    "assets/audio/idleBackgroundMusic_1.m4a", // TRACK SOURCE FILE
                    "assets/audio/idleBackgroundMusic_2.m4a", // TRACK SOURCE FILE
                    "assets/audio/idleBackgroundMusic_3.m4a" // TRACK SOURCE FILE
                ],
                "IdleBackgroundMusicSequencer", // THIS IS THE AUDIO SEQUENCER CONCRETE CLASS
                "119.57", // THIS IS THE TEMPO
                "3" // THIS IS BPS (beats per second [BPM / 60]) MULTIPLIER
                ],

                // Regular music
                ["fsBackgroundMusic_0", "assets/audio/fsBackgroundMusic_0.m4a"]
            ],
            */

            throw new Error("AudioSequencer was unable to autodetect the tempo! Please provide a value for the tempo at asset-config.json!");
        }

        const averageTempo = sum / count;
        return averageTempo;
    }
}